import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../redux'; // Fixed import path
import {
  fetchHealth,
  fetchScraperStatus,
  fetchConnection,
  runScraper,
  runScraperSync,
  scrapeImages,
  scrapeBio,
  fetchClinicians,
  fetchClinicianProfiles,
  fetchScraperRuns,
  fetchScraperRunDetails,
  fetchCronJobs,
  fetchCronJobDetails,
  createCronJob,
  startCronJob,
  stopCronJob,
  deleteCronJob,
  triggerCronJob,
  clearErrors,
  resetScraperResults,
  createIntegrationWithScraper
} from '../../redux/scraper/scraperSlice'; // Adjust import path as needed

// Hook for health monitoring
export const useScraperHealth = () => {
  const dispatch = useDispatch<AppDispatch>();
  const health = useSelector((state: RootState) => state.scraper.health);
  
  const refetch = () => dispatch(fetchHealth());
  
  return { ...health, refetch };
};

// Hook for scraper status
export const useScraperStatus = () => {
  const dispatch = useDispatch<AppDispatch>();
  const status = useSelector((state: RootState) => state.scraper.status);
  
  const refetch = () => dispatch(fetchScraperStatus());
  
  return { ...status, refetch };
};

// Hook for all scraper operations
export const useScraperOperations = () => {
  const dispatch = useDispatch<AppDispatch>();
  const scraperRun = useSelector((state: RootState) => state.scraper.scraperRun);
  const scraperRunSync = useSelector((state: RootState) => state.scraper.scraperRunSync);
  const imageScraper = useSelector((state: RootState) => state.scraper.imageScraper);
  const bioScraper = useSelector((state: RootState) => state.scraper.bioScraper);
  const createIntegrationWithScraperState = useSelector((state: RootState) => state.scraper.createIntegrationWithScraper);
  
  const fetchConnectionAsync = (params: { practiceId?: string; username?: string; password?: string }) => 
    dispatch(fetchConnection(params));
  const runScraperAsync = (practiceId?: string, username?: string, password?: string) => 
    dispatch(runScraper({ practiceId, username, password }));
  const runScraperSyncAsync = (practiceId?: string, username?: string, password?: string) => 
    dispatch(runScraperSync({ practiceId, username, password }));
  const scrapeImagesAsync = (params: { practiceId?: string; clinicianId?: string; forceUpdate?: boolean }) => 
    dispatch(scrapeImages(params));
  const scrapeBioAsync = (practiceId: string) => dispatch(scrapeBio(practiceId));
  const resetResults = () => dispatch(resetScraperResults());
  const createIntegrationWithScraperAsync = (params: { 
    integration: any; 
    practiceId: string; 
    username: string; 
    password: string 
  }) => dispatch(createIntegrationWithScraper(params));
  
  return {
    scraperRun,
    scraperRunSync,
    imageScraper,
    bioScraper,
    createIntegrationWithScraper: createIntegrationWithScraperState,
    fetchConnectionAsync,
    runScraperAsync,
    runScraperSyncAsync,
    scrapeImagesAsync,
    scrapeBioAsync,
    createIntegrationWithScraperAsync,
    resetResults
  };
};

// Hook for clinician availability data
export const useClinicians = () => {
  const dispatch = useDispatch<AppDispatch>();
  const clinicians = useSelector((state: RootState) => state.scraper.clinicians);
  
  const fetch = (params?: { date?: string; clinician?: string; limit?: number }) => 
    dispatch(fetchClinicians(params || {}));
  
  return { ...clinicians, fetch };
};

// Hook for clinician profiles
export const useClinicianProfiles = () => {
  const dispatch = useDispatch<AppDispatch>();
  const profiles = useSelector((state: RootState) => state.scraper.clinicianProfiles);
  
  const fetch = (params?: { practiceId?: string; name?: string; limit?: number }) => 
    dispatch(fetchClinicianProfiles(params || {}));
  
  return { ...profiles, fetch };
};

// Hook for scraper run history and details
export const useScraperRuns = () => {
  const dispatch = useDispatch<AppDispatch>();
  const runs = useSelector((state: RootState) => state.scraper.scraperRuns);
  const runDetails = useSelector((state: RootState) => state.scraper.scraperRunDetails);
  
  const fetchRuns = (params?: { practiceId?: string; status?: string; limit?: number; includeFullResult?: boolean }) => 
    dispatch(fetchScraperRuns(params || {}));
  const fetchDetails = (runId: string) => dispatch(fetchScraperRunDetails(runId));
  
  return { runs, runDetails, fetchRuns, fetchDetails };
};

// Hook for cron job management
export const useCronJobs = () => {
  const dispatch = useDispatch<AppDispatch>();
  const cronJobs = useSelector((state: RootState) => state.scraper.cronJobs);
  const cronJobDetails = useSelector((state: RootState) => state.scraper.cronJobDetails);
  const operations = useSelector((state: RootState) => state.scraper.cronOperations);
  
  const fetchJobs = () => dispatch(fetchCronJobs());
  const fetchJobDetails = (jobId: number) => dispatch(fetchCronJobDetails(jobId));
  const createJob = (params: { schedule: string; practiceId?: string; name?: string; description?: string }) =>
    dispatch(createCronJob(params));
  const startJob = (jobId: number) => dispatch(startCronJob(jobId));
  const stopJob = (jobId: number) => dispatch(stopCronJob(jobId));
  const deleteJob = (jobId: number) => dispatch(deleteCronJob(jobId));
  const triggerJob = (jobId: number) => dispatch(triggerCronJob(jobId));
  
  return {
    cronJobs,
    cronJobDetails,
    operations,
    fetchJobs,
    fetchJobDetails,
    createJob,
    startJob,
    stopJob,
    deleteJob,
    triggerJob
  };
};

// Hook for error management
export const useScraperErrors = () => {
  const dispatch = useDispatch<AppDispatch>();
  const clear = () => dispatch(clearErrors());
  
  const errors = useSelector((state: RootState) => ({
    health: state.scraper.health.error,
    status: state.scraper.status.error,
    scraperRun: state.scraper.scraperRun.error,
    scraperRunSync: state.scraper.scraperRunSync.error,
    imageScraper: state.scraper.imageScraper.error,
    bioScraper: state.scraper.bioScraper.error,
    clinicians: state.scraper.clinicians.error,
    clinicianProfiles: state.scraper.clinicianProfiles.error,
    scraperRuns: state.scraper.scraperRuns.error,
    scraperRunDetails: state.scraper.scraperRunDetails.error,
    cronJobs: state.scraper.cronJobs.error,
    cronJobDetails: state.scraper.cronJobDetails.error,
    cronOperations: state.scraper.cronOperations
  }));
  
  return { errors, clear };
};

// Convenience hook for loading states
export const useScraperLoading = () => {
  return useSelector((state: RootState) => ({
    health: state.scraper.health.loading,
    status: state.scraper.status.loading,
    scraperRun: state.scraper.scraperRun.loading,
    scraperRunSync: state.scraper.scraperRunSync.loading,
    imageScraper: state.scraper.imageScraper.loading,
    bioScraper: state.scraper.bioScraper.loading,
    clinicians: state.scraper.clinicians.loading,
    clinicianProfiles: state.scraper.clinicianProfiles.loading,
    scraperRuns: state.scraper.scraperRuns.loading,
    scraperRunDetails: state.scraper.scraperRunDetails.loading,
    cronJobs: state.scraper.cronJobs.loading,
    cronJobDetails: state.scraper.cronJobDetails.loading,
    cronOperations: {
      create: state.scraper.cronOperations.create.loading,
      start: state.scraper.cronOperations.start.loading,
      stop: state.scraper.cronOperations.stop.loading,
      delete: state.scraper.cronOperations.delete.loading,
      trigger: state.scraper.cronOperations.trigger.loading
    }
  }));
};

// Combined hook for comprehensive scraper overview
export const useScraperOverview = () => {
  const health = useScraperHealth();
  const status = useScraperStatus();
  const loading = useScraperLoading();
  const { errors, clear: clearErrors } = useScraperErrors();
  
  // Check if any critical operations are running
  const isAnyOperationRunning = loading.scraperRun || loading.scraperRunSync || 
    loading.imageScraper || loading.bioScraper || status.data?.running;
  
  // Get overall system health
  const systemHealthy = health.data?.status === 'healthy' && !health.error;
  
  return {
    health,
    status,
    loading,
    errors,
    clearErrors,
    isAnyOperationRunning,
    systemHealthy
  };
};